/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { Switch, Route, useParams } from 'react-router-dom';
import SurveyContainer from '@features/survey-container/SurveyContainer';
import { SurveyConfiguration } from '@features/survey-configuration/SurveyConfiguration';
import SurveyAssurance from '@features/survey/assurance/SurveyAssurance';
import QuestionContainer from '../../features/survey/question/QuestionContainer';
import SurveyScope from '@features/survey-scope/SurveyScope';
import SurveyDelegation from '@apps/company-tracker/components/delegation/survey/survey-delegation/SurveyDelegation';
import SurveyImportExport from './survey-import-export';
import { sidebarPath } from '@features/survey-settings-toolbar/breadcrumbPath';
import { SurveyAddAssurance } from '../../types/survey';
import { ROUTES } from '../../constants/routes';
import { SurveyOverviewWithBlockLoading } from '@features/survey/overview/SurveyOverviewWithBlockLoading';
import { AIAutoAnswerSupervisor } from '@features/ai-auto-answer-supervisor/AIAutoAnswerSupervisor';
import { generateUrl } from '../util';

interface SurveyComponentProps {
  initiativeId: string;
  surveyId: string;
  page?: string;
  batchId?: string;
  handleNavigate: (page: string, extras: { [key: string]: string }) => void;
}

const SurveyComponent = ({ initiativeId, surveyId, page, batchId, handleNavigate }: SurveyComponentProps) => {
  switch (page) {
    case 'configuration':
      return <SurveyConfiguration />
    case 'scope':
      return <SurveyScope />
    case 'delegation':
      return <SurveyDelegation />
    case 'import-export':
      return <SurveyImportExport />
    case 'auto-answer':
      return <AIAutoAnswerSupervisor batchId={batchId} initiativeId={initiativeId} surveyId={surveyId} handleNavigate={handleNavigate} />
    case 'overview':
    default:
      return <SurveyOverviewWithBlockLoading />
  }
}

export const CompanyTrackerSurveyRoute = (props: { handleAddAssurance: (survey: SurveyAddAssurance) => void }) => {

  const { path } = useRouteMatch();
  const { initiativeId, surveyId, page, view } = useParams<{ initiativeId: string; surveyId: string; page?: string; view?: string }>();
  const history = useHistory();

  const handleNavigate = (page: string, extras: { [key: string]: string }) => {
    // Map batchId to view param for auto-answer (TODO: refactor to dedicated route)
    const { batchId, ...rest } = extras;
    history.push(generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId, surveyId, page, ...rest, ...(page === 'auto-answer' && batchId && { view: batchId }) }));
  };
  return (
    <Switch>
      <Route path={`${path}/question/:questionId/:questionIndex/draft`}>
        <QuestionContainer isDraft={true} handleAddAssurance={props.handleAddAssurance} />
      </Route>

      <Route path={`${path}/question/:questionId/:questionIndex?`}>
        <QuestionContainer handleAddAssurance={props.handleAddAssurance} />
      </Route>
      <Route path={ROUTES.COMPANY_TRACKER_ASSURANCE.path}>
        <SurveyAssurance surveyId={surveyId} />
      </Route>

      <Route path={`${path}/${sidebarPath}`}>
        <SurveyContainer handleNavigate={handleNavigate}>
          <SurveyComponent initiativeId={initiativeId} surveyId={surveyId} page={page} batchId={view} handleNavigate={handleNavigate} />
        </SurveyContainer>
      </Route>
    </Switch>
  );
}
